import { DashboardFilters } from "@/components/dashboard/dashboard-filters";
import { BrowserExtensionsSetup } from "@/components/non_registrant/browser-extensions-setup";
import { DashboardOrganizations } from "@/components/non_registrant/dashboard-organizations";
import { DashboardParties } from "@/components/non_registrant/dashboard-parties";
import { DashboardRegistrants } from "@/components/non_registrant/dashboard-registrants";
import { DashboardUsers } from "@/components/non_registrant/dashboard-users";
import { OrganizationDetailsSheet } from "@/components/non_registrant/organization-details-sheet";
import { RegistrantDetailsSheet } from "@/components/non_registrant/registrant-details-sheet";
import PageHeader from "@/components/page-header";
import {
  SETUP_PAGE_CHROME_CLASS,
  SETUP_PAGE_LIST_TAB_HOSTS_CHROME_CLASS,
  SETUP_PAGE_SCROLL_TAB_CLASS,
  SETUP_PAGE_SHELL_CLASS,
  SETUP_PAGE_TABS_CLASS,
} from "@/components/portal/setup-page-layout";
import {
  SETUP_TABS_LIST_CLASS,
  SETUP_TAB_TRIGGER_CLASS,
  SetupPageTabBar,
} from "@/components/portal/setup-page-tab-bar";
import LoadingScreen from "@/components/ui/loading-screen";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserType } from "@/constants";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useGovernmentDashboardStats } from "@/hooks/use-government-dashboard-stats";
import { useGovernmentLicenseTypeFilterOptions } from "@/hooks/use-government-license-type-filter-options";
import { DASHBOARD_STATS_TAB_PRELOAD_ALL } from "@/lib/dashboard-query-options";
import { getDashboardLink } from "@/lib/utils";
import { SESSION_KEYS, sessionStorage } from "@/types/session-storage";
import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/government/setup")({
  validateSearch: (search: Record<string, unknown>) => {
    const tab = (search.tab as string) || "government-users";
    return {
      tab: tab === "users" ? "government-users" : tab,
    };
  },
  component: GovernmentSetupPage,
});

const SETUP_TABS = [
  { value: "hera-operators", label: "Hera Operators" },
  { value: "government-users", label: "Government Users" },
  { value: "registrants", label: "Registrants" },
  { value: "organizations", label: "Organizations" },
  { value: "parties", label: "Parties" },
  { value: "extensions", label: "Extensions" },
] as const;

export function GovernmentSetupPage() {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const { tab: searchTab } = Route.useSearch();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(
    searchTab || "government-users",
  );

  const [selectedRegistrantId, setSelectedRegistrantId] = useState<
    string | null
  >(null);
  const [isRegistrantSheetOpen, setIsRegistrantSheetOpen] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    string | null
  >(null);
  const [isOrganizationSheetOpen, setIsOrganizationSheetOpen] = useState(false);

  const { licenseTypeOptions } = useGovernmentLicenseTypeFilterOptions();
  const { jurisdictionsData } = useGovernmentDashboardStats(
    isAuthSetupComplete,
    [], // no license type filter for setup
    DASHBOARD_STATS_TAB_PRELOAD_ALL,
  );

  const [selectedLicenseTypes, setSelectedLicenseTypes] = useState<string[]>(
    () =>
      sessionStorage.get(SESSION_KEYS.GOVERNMENT_SELECTED_LICENSE_TYPES, []),
  );

  const shouldShowLicenseTypeFilter = licenseTypeOptions.length > 1;

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.GOVERNMENT_SELECTED_LICENSE_TYPES,
      selectedLicenseTypes,
    );
  }, [selectedLicenseTypes]);

  // Government users see only their jurisdiction(s); no jurisdiction filter (like dashboard)
  const effectiveJurisdictions = jurisdictionsData.map((j) => j.id);

  useEffect(() => {
    if (searchTab && searchTab !== selectedTab) {
      setSelectedTab(searchTab);
    }
  }, [searchTab, selectedTab]);

  const handleTabChange = (value: string) => {
    setSelectedTab(value);
    const currentPath = location.pathname;
    navigate({
      to: currentPath,
      search: { tab: value },
      replace: true,
    });
  };

  if (!isAuthSetupComplete) {
    return <LoadingScreen message="Authenticating..." />;
  }

  // These lists render the page chrome inside their own scroller so the title
  // scrolls away with the rows, like the license list.
  const listHostsPageChrome =
    selectedTab === "parties" ||
    selectedTab === "hera-operators" ||
    selectedTab === "government-users" ||
    selectedTab === "registrants" ||
    selectedTab === "organizations";

  const pageChrome = (
    <>
      <PageHeader
        className={SETUP_PAGE_CHROME_CLASS}
        title="Setup"
        hideBackButton
        subtitle="Users, registrants, organizations and parties for your jurisdiction"
        action={
          selectedTab === "extensions" ? undefined : (
            <DashboardFilters
              selectedLicenseTypes={selectedLicenseTypes}
              onSelectedLicenseTypesChange={setSelectedLicenseTypes}
              licenseTypeOptions={licenseTypeOptions}
              showLicenseTypeFilter={shouldShowLicenseTypeFilter}
            />
          )
        }
      />
      <p className="shrink-0 text-muted-foreground text-sm mt-2">
        {
          {
            "hera-operators": "View Hera operator user accounts.",
            "government-users":
              "Manage government user accounts for your jurisdiction.",
            registrants: "View registrant accounts in your jurisdiction.",
            organizations: "View organizations in your jurisdiction.",
            parties:
              "View party types used as selectable options when answering questions during registration, de-registration, and inspection.",
            extensions:
              "Install Hera Court Link so EXTENSION live links open court case search.",
          }[selectedTab]
        }
      </p>
    </>
  );

  return (
    <div className={SETUP_PAGE_SHELL_CLASS}>
      <Tabs
        value={selectedTab}
        onValueChange={handleTabChange}
        className={SETUP_PAGE_TABS_CLASS}
      >
        <SetupPageTabBar dashboardLink={getDashboardLink(UserType.GOVERNMENT)}>
          <TabsList className={SETUP_TABS_LIST_CLASS}>
            {SETUP_TABS.map(({ value, label }) => (
              <TabsTrigger
                key={value}
                className={SETUP_TAB_TRIGGER_CLASS}
                value={value}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </SetupPageTabBar>

        {listHostsPageChrome ? null : pageChrome}

        <TabsContent
          value="hera-operators"
          className={SETUP_PAGE_LIST_TAB_HOSTS_CHROME_CLASS}
        >
          <DashboardUsers
            userType={UserType.GOVERNMENT}
            isAuthSetupComplete={isAuthSetupComplete}
            selectedJurisdictions={effectiveJurisdictions}
            filterByUserType={UserType.OPERATOR}
            leadingContent={pageChrome}
          />
        </TabsContent>

        <TabsContent
          value="government-users"
          className={SETUP_PAGE_LIST_TAB_HOSTS_CHROME_CLASS}
        >
          <DashboardUsers
            userType={UserType.GOVERNMENT}
            isAuthSetupComplete={isAuthSetupComplete}
            selectedJurisdictions={effectiveJurisdictions}
            filterByUserType={UserType.GOVERNMENT}
            leadingContent={pageChrome}
          />
        </TabsContent>

        <TabsContent
          value="registrants"
          className={SETUP_PAGE_LIST_TAB_HOSTS_CHROME_CLASS}
        >
          <DashboardRegistrants
            isAuthSetupComplete={isAuthSetupComplete}
            staffPortal={UserType.GOVERNMENT}
            leadingContent={pageChrome}
            selectedRegistrantId={selectedRegistrantId}
            onRegistrantSelect={(id) => {
              setSelectedRegistrantId(id);
              setIsRegistrantSheetOpen(true);
            }}
            isRegistrantSheetOpen={isRegistrantSheetOpen}
            onRegistrantSheetOpenChange={setIsRegistrantSheetOpen}
            onOpenOrganization={(orgId) => {
              setSelectedOrganizationId(orgId);
              setIsOrganizationSheetOpen(true);
              setIsRegistrantSheetOpen(false);
            }}
          />
        </TabsContent>

        <TabsContent
          value="organizations"
          className={SETUP_PAGE_LIST_TAB_HOSTS_CHROME_CLASS}
        >
          <DashboardOrganizations
            isAuthSetupComplete={isAuthSetupComplete}
            jurisdictionIds={effectiveJurisdictions}
            leadingContent={pageChrome}
            selectedOrganizationId={selectedOrganizationId}
            onOrganizationSelect={(id) => {
              setSelectedOrganizationId(id);
              setIsOrganizationSheetOpen(true);
            }}
            isOrganizationSheetOpen={isOrganizationSheetOpen}
            onOrganizationSheetOpenChange={setIsOrganizationSheetOpen}
            onOpenRegistrant={(registrantId) => {
              setSelectedRegistrantId(registrantId);
              setIsRegistrantSheetOpen(true);
              setIsOrganizationSheetOpen(false);
            }}
          />
        </TabsContent>

        <TabsContent
          value="parties"
          className={SETUP_PAGE_LIST_TAB_HOSTS_CHROME_CLASS}
        >
          <DashboardParties
            isAuthSetupComplete={isAuthSetupComplete}
            canWriteParties={false}
            helpPortal="government"
            leadingContent={<div className="pb-4">{pageChrome}</div>}
          />
        </TabsContent>

        <TabsContent value="extensions" className={SETUP_PAGE_SCROLL_TAB_CLASS}>
          <BrowserExtensionsSetup />
        </TabsContent>
      </Tabs>

      <RegistrantDetailsSheet
        isOpen={isRegistrantSheetOpen}
        onOpenChange={setIsRegistrantSheetOpen}
        registrantAccountId={selectedRegistrantId}
        onOpenOrganization={(orgId) => {
          setSelectedOrganizationId(orgId);
          setIsOrganizationSheetOpen(true);
          setIsRegistrantSheetOpen(false);
        }}
        key={selectedRegistrantId ?? "registrant-sheet"}
        staffPortal={UserType.GOVERNMENT}
      />
      <OrganizationDetailsSheet
        isOpen={isOrganizationSheetOpen}
        onOpenChange={setIsOrganizationSheetOpen}
        organizationId={selectedOrganizationId}
        onOpenRegistrant={(registrantId) => {
          setSelectedRegistrantId(registrantId);
          setIsRegistrantSheetOpen(true);
          setIsOrganizationSheetOpen(false);
        }}
        key={selectedOrganizationId ?? "organization-sheet"}
      />
    </div>
  );
}
